import { useMemo } from "react";
import { useIntl } from "@leagueplatform/locales";
import { useForm } from "@leagueplatform/web-common";
import { StackLayout, Box } from "@leagueplatform/genesis-core";
import { FormContainer } from "components/forms/form-container/form-container.component";
import { RHFTextInput } from "components/form-elements/rhf-text-input.component";
import { RHFSelect } from "components/form-elements/rhf-select.component";
import { US_STATES } from "common/constants/form-options";
import type { ProfileData } from "types/types";

export interface MailingAddressSectionProps {
  data: ProfileData | undefined;
  onSubmit: (values: MailingAddressFormValues) => Promise<void>;
}

export type MailingAddressFormValues = {
  streetAddress: string;
  aptSuiteUnit: string;
  city: string;
  state: string;
  zipCode: string;
};

const composeDisplayAddress = (address?: {
  streetAddress?: string;
  aptSuiteUnit?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}): string | undefined => {
  if (!address) return undefined;

  const { streetAddress, aptSuiteUnit, city, state, zipCode } = address;
  const parts = [];

  if (streetAddress) parts.push(streetAddress);
  if (aptSuiteUnit) parts.push(aptSuiteUnit);

  const cityStateZip = [city, state].filter(Boolean).join(", ");
  const locationLine = [cityStateZip, zipCode].filter(Boolean).join("  ");

  if (locationLine) parts.push(locationLine);

  return parts.length > 0 ? parts.join("\n") : undefined;
};

export const MailingAddressSection = ({
  data,
  onSubmit,
}: MailingAddressSectionProps) => {
  const { formatMessage } = useIntl();

  const defaultValues: MailingAddressFormValues = useMemo(
    () => ({
      streetAddress: data?.mailingAddress?.streetAddress || "",
      aptSuiteUnit: data?.mailingAddress?.aptSuiteUnit || "",
      city: data?.mailingAddress?.city || "",
      state: data?.mailingAddress?.state || "",
      zipCode: data?.mailingAddress?.zipCode || "",
    }),
    [data]
  );

  const formMethods = useForm<MailingAddressFormValues>({
    defaultValues,
  });

  const profileFieldValues = useMemo(() => {
    if (!data) return [];

    return [
      {
        label: formatMessage({ id: "STR_ADDRESS" }),
        value: composeDisplayAddress(data.mailingAddress),
        isLocked: data.isAddressLocked,
      },
    ];
  }, [data, formatMessage]);

  return (
    <FormContainer
      heading={formatMessage({ id: "STR_MAILING_ADDRESS" })}
      profileFieldValues={profileFieldValues}
      formMethods={formMethods}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
    >
      <StackLayout spacing="$one" horizontalAlignment="stretch">
        {/* Row 1: Street Address + Apt/Suite */}
        <Box
          css={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "$one",
            "@mobile": { gridTemplateColumns: "1fr" },
          }}
        >
          <RHFTextInput
            id="streetAddress"
            label={formatMessage({ id: "STR_STREET_ADDRESS" })}
            required
            requiredMessage={formatMessage(
              { id: "STR_PLEASE_ENTER_FIELD" },
              {
                label: formatMessage({
                  id: "STR_STREET_ADDRESS",
                }).toLowerCase(),
              }
            )}
          />
          <RHFTextInput
            required={false}
            id="aptSuiteUnit"
            label={formatMessage({ id: "STR_APT_SUITE_UNIT" })}
          />
        </Box>
        {/* Row 2: City + State */}
        <Box
          css={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "$one",
            "@mobile": { gridTemplateColumns: "1fr" },
          }}
        >
          <RHFTextInput
            id="city"
            label={formatMessage({ id: "STR_CITY" })}
            required
            requiredMessage={formatMessage(
              { id: "STR_PLEASE_ENTER_FIELD" },
              { label: formatMessage({ id: "STR_CITY" }).toLowerCase() }
            )}
          />
          <RHFSelect
            id="state"
            label={formatMessage({ id: "STR_STATE" })}
            options={US_STATES}
            placeholder={formatMessage({ id: "STR_SELECT_STATE" })}
            required
            requiredMessage={formatMessage(
              { id: "STR_PLEASE_ENTER_FIELD" },
              { label: formatMessage({ id: "STR_STATE" }).toLowerCase() }
            )}
          />
        </Box>
        {/* Row 3: Zip Code (single column) */}
        <Box
          css={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "$one",
            "@mobile": { gridTemplateColumns: "1fr" },
          }}
        >
          <RHFTextInput
            id="zipCode"
            label={formatMessage({ id: "STR_ZIP" })}
            required
            requiredMessage={formatMessage(
              { id: "STR_PLEASE_ENTER_FIELD" },
              { label: formatMessage({ id: "STR_ZIP_CODE" }) }
            )}
          />
        </Box>
      </StackLayout>
    </FormContainer>
  );
};
